import TemplateContents from './TemplateContents';
import * as THREE from 'three';
import vertex from './shaders/vertex_mask4.glsl';
import fragment from './shaders/frag_mask4.glsl';

export default class extends TemplateContents{
    constructor(param){
        super(param);
    }

    init() {
        super.init(true);

        this.setVars();
        this.setDom();
        if(!this.sectionDOM || !this.canvasContainer || !this.videoDOM){
            this.isReady = false;
            return;
        }

        this.isReady = true;
        this.canvas = document.createElement("canvas");
        this.canvasContainer.appendChild(this.canvas);
        this.sectionDOM.classList.add('has-canvas');

        this.sw = this.canvasContainer.offsetWidth;
        this.sh = this.canvasContainer.offsetHeight;
        this.canvas.width = this.sw;
        this.canvas.height = this.sh;

        this.initWebGL();
        this.initMesh();
        this.resetVideo();
        this.initEvents();
    }

    setVars(){
        super.setVars();
        this.videoEnded = false;
        this.startedAnimation = false;
        this.hasTriggered = false;
        this.intersectionObserver = null;
        this.bindVisibilityScrollHandler = null;
    }

    setDom(){
        super.setDom();
        this.sectionDOM = document.querySelector('.section-entry');
        if(!this.sectionDOM){
            this.canvasContainer = null;
            this.videoDOM = null;
            return;
        }

        this.canvasContainer = this.sectionDOM.querySelector('.mask_target');
        this.videoDOM = this.sectionDOM.querySelector('video');
    }

    start(){
        if(!this.isReady || !this.videoDOM) return;

        this.setupVisibilityTrigger();
        this.checkVisibilityAndStart();
    }

    setupVisibilityTrigger(){
        if(!this.sectionDOM) return;

        if('IntersectionObserver' in window){
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if(entry.isIntersecting){
                            this.triggerAnimation();
                        }
                    });
                },
                {
                    root: null,
                    threshold: 0.2,
                    rootMargin: '0px 0px -12% 0px',
                }
            );
            this.intersectionObserver.observe(this.sectionDOM);
            return;
        }

        this.bindVisibilityScrollHandler = this.checkVisibilityAndStart.bind(this);
        window.addEventListener('scroll', this.bindVisibilityScrollHandler, { passive: true });
        window.addEventListener('resize', this.bindVisibilityScrollHandler);
    }

    teardownVisibilityTrigger(){
        if(this.intersectionObserver){
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }

        if(this.bindVisibilityScrollHandler){
            window.removeEventListener('scroll', this.bindVisibilityScrollHandler);
            window.removeEventListener('resize', this.bindVisibilityScrollHandler);
            this.bindVisibilityScrollHandler = null;
        }
    }

    checkVisibilityAndStart(){
        if(!this.sectionDOM || this.hasTriggered) return;

        const rect = this.sectionDOM.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const isVisible = rect.top < viewportHeight * 0.8 && rect.bottom > viewportHeight * 0.2;
        if(isVisible){
            this.triggerAnimation();
        }
    }

    triggerAnimation(){
        if(this.hasTriggered) return;
        this.hasTriggered = true;
        this.sectionDOM.classList.add('show');
        this.teardownVisibilityTrigger();

        this.videoEnded = false;
        if(this.bindEndedHandler) this.videoDOM.removeEventListener('ended', this.bindEndedHandler);
        this.bindEndedHandler = () => { this.videoEnded = true; };
        this.videoDOM.addEventListener('ended', this.bindEndedHandler);

        try {
            this.videoDOM.currentTime = 0;
        } catch (error) {
            // ignore currentTime seek errors before metadata load
        }

        const playPromise = this.videoDOM.play();
        if(playPromise && typeof playPromise.catch === 'function'){
            playPromise.catch(() => {});
        }
        this.enterframe();
        this.startedAnimation = true;
    }

    destruct(){
        this.teardownVisibilityTrigger();

        if(this.bindEndedHandler && this.videoDOM){
            this.videoDOM.removeEventListener('ended', this.bindEndedHandler);
        }

        super.destruct();
    }

    destructAfterContentsOut(){
        if(this.sectionDOM){
            this.sectionDOM.classList.remove('has-canvas');
            this.sectionDOM.classList.remove('show');
        }

        if(this.videoTexture){
            this.videoTexture.dispose();
            this.videoTexture = null;
        }

        if(this.material && this.material.uniforms && this.material.uniforms.uVideoTexture){
            this.material.uniforms.uVideoTexture.value = null;
        }

        if(this.canvas && this.canvas.parentNode){
            this.canvas.parentNode.removeChild(this.canvas);
        }
    }

    initWebGL() {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            alpha : true,
            antialias : true
        });
        this.renderer.setPixelRatio(1);
        this.renderer.setSize(this.sw, this.sh);
        this.renderer.autoClear = false;

        this.scene = new THREE.Scene();

        let frustumSize = 1;
        this.camera = new THREE.OrthographicCamera(
            frustumSize / -2,
            frustumSize / 2,
            frustumSize / 2,
            frustumSize / -2,
            -1000,
            1000
        );
        this.camera.position.set(0,0,2);
    }

    getScaleAdjust(){
        const textureAspect = 1300 / 700;
        return { textureAspect, scale: 1.06, adjustX: -.002, adjustY: 0.01 };
    }

    getCanvasAdjust(isSP, sw, sh){
        if(!isSP){
            const pcScale = 0.75;
            return { canvasSw: sw * pcScale, canvasSh: sh * pcScale };
        }else{
            const spScale = 1.25;
            return { canvasSw: sw * spScale, canvasSh: sh * spScale };
        }
    }

    initMesh(){
        const { textureAspect, scale, adjustX, adjustY } = this.getScaleAdjust();

        let geometry = new THREE.PlaneGeometry( 1, 1, 32 );
        let material = this.material = new THREE.ShaderMaterial({
            side: THREE.DoubleSide,
            uniforms:{
                uViewport: {value: new THREE.Vector2(this.sw, this.sh)},
                uVideoTexture: {value: null},
                uTextureAspect: {value: textureAspect},
                uScale: {value: scale},
                uAdjust: {value: new THREE.Vector2(adjustX, adjustY)},
            },
            vertexShader: vertex,
            fragmentShader: fragment,
            transparent: true
        });

        let plane = new THREE.Mesh( geometry, material );
        this.scene.add( plane );
    }

    resetVideo(){
        if(!this.isReady || !this.videoDOM || !this.material) return;

        if(this.videoTexture){
            this.videoTexture.dispose();
        }

        this.videoTexture = new THREE.VideoTexture(this.videoDOM);
        this.material.uniforms.uVideoTexture.value = this.videoTexture;
        this.render();
    }

    render(){
        if(!this.isReady || !this.renderer || !this.scene || !this.camera) return;
        this.renderer.render(this.scene, this.camera);
    }

    enterframe(){
        if(this.videoEnded) {
            this.enterframeID = undefined;
            return;
        }
        this.enterframeID = window.requestAnimationFrame(this.enterframe.bind(this));
        this.enterframeThinOut();
    }

    enterframeThinOut(){
        if(!this.isReady || this.videoEnded) return;
        this.render();
    }

    executeResize() {
        if(!this.isReady || !this.canvasContainer) return;

        const sw = this.canvasContainer.offsetWidth;
        const sh = this.canvasContainer.offsetHeight;
        const isSP = sw < this.pack.BP;
        const { canvasSw, canvasSh } = this.getCanvasAdjust(isSP, sw, sh);

        this.sw = canvasSw;
        this.sh = canvasSh;
        this.swh = this.sw / 2;
        this.shh = this.sh / 2;

        if(!this.canvas) return;
        this.canvas.width = this.sw;
        this.canvas.height = this.sh;

        if(!this.camera || !this.renderer || !this.material) return;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.sw, this.sh);
        this.material.uniforms.uViewport.value = new THREE.Vector2(this.sw, this.sh);
        this.render();
    }
}
